import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useRoles } from './context/RolesContext';
import './NavBar.css';

const MIN_SCALE = 70;
const MAX_SCALE = 200;
const SCALE_STEP = 10;

function NavBarThing() {
    const isAuthenticated = useIsAuthenticated();
    const { instance } = useMsal();
    const location = useLocation();

    const [scale, setScale] = useState(() =>
        parseInt(localStorage.getItem('ui-scale') || '100', 10)
    );

    const [theme, setTheme] = useState(
        () => localStorage.getItem('ui-theme') || 'dark'
    );

    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
    }, [theme]);

    useEffect(() => {
        const handleMouseDown = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleMouseDown);
        return () => document.removeEventListener('mousedown', handleMouseDown);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 1024) {
                const el = document.getElementById('zoom-root');
                if (el) el.style.zoom = 1;
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleToggleTheme = () => {
        setTheme(prev => {
            const next = prev === 'dark' ? 'light' : 'dark';
            localStorage.setItem('ui-theme', next);
            return next;
        });
    };

    const applyScale = (next) => {
        const el = document.getElementById('zoom-root');
        if (el) el.style.zoom = next / 100;
        localStorage.setItem('ui-scale', next);
        setScale(next);
    };

    const { isAdmin } = useRoles();

    const handleLogout = () => {
        instance.logoutRedirect();
    };

    const isActive = (path) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname.startsWith(path);
    };

    const navLinks = [
        { to: '/', label: 'Home' },
        { to: '/about', label: 'About' },
        { to: '/study', label: 'Study' },
        { to: '/faq', label: 'FAQ' },
    ];

    return (
        <nav className="navbar" ref={menuRef}>
            <div className="navbar-inner">

                <Link to="/" className="navbar-logo">
                    <span className="navbar-logo-cert">CERT</span>
                    <span className="navbar-logo-students">DRILL</span>
                </Link>

                <button
                    className="navbar-hamburger"
                    onClick={() => setMenuOpen(prev => !prev)}
                    aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={menuOpen}
                >
                    {menuOpen ? '✕' : '☰'}
                </button>

                <ul className={`navbar-links${menuOpen ? ' navbar-links--open' : ''}`}>
                    {navLinks.map(({ to, label }) => (
                        <li key={to}>
                            <Link
                                to={to}
                                className={`navbar-link${isActive(to) ? ' active' : ''}`}
                                onClick={() => setMenuOpen(false)}
                            >
                                {label}
                            </Link>
                        </li>
                    ))}
                </ul>

                <div className="navbar-auth">
                    <div className="navbar-scale">
                        <button
                            className="navbar-scale-btn"
                            onClick={() => applyScale(scale - SCALE_STEP)}
                            disabled={scale <= MIN_SCALE}
                            aria-label="Decrease UI scale"
                        >−</button>
                        <span className="navbar-scale-label">{scale}%</span>
                        <button
                            className="navbar-scale-btn"
                            onClick={() => applyScale(scale + SCALE_STEP)}
                            disabled={scale >= MAX_SCALE}
                            aria-label="Increase UI scale"
                        >+</button>
                        <button
                            className="navbar-theme-btn"
                            onClick={handleToggleTheme}
                            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
                        >
                            {theme === 'dark' ? '☀' : '☾'}
                        </button>
                    </div>
                    {!isAuthenticated ? (
                        <Link to="/login" className="navbar-btn-signin">
                            Sign In →
                        </Link>
                    ) : (
                        <>
                            {isAdmin && (
                                <Link
                                    to="/admin"
                                    className={`navbar-link${location.pathname.startsWith('/admin') ? ' active' : ''}`}
                                >
                                    Admin
                                </Link>
                            )}
                            <button
                                className="navbar-btn-feedback"
                                onClick={() => window.dispatchEvent(new CustomEvent('smartstudy:open-feedback'))}
                            >
                                Feedback
                            </button>
                            <Link
                                to="/profile"
                                className={`navbar-link${isActive('/profile') ? ' active' : ''}`}
                            >
                                Profile
                            </Link>
                            <button className="navbar-btn-signout" onClick={handleLogout}>
                                Sign Out
                            </button>
                        </>
                    )}
                </div>

            </div>
        </nav>
    );
}

export default NavBarThing;
